import { z } from 'zod';

// ---------------------------------------------------------------------------
// Live Update Navigation
// ---------------------------------------------------------------------------

// User-facing nav values only. `chat_task` is an internal cache-sharing
// fetch mode (never a persisted nav state), so it is intentionally excluded.
export const navItemSchema = z.enum(['chat', 'support', 'task']);

export const hubCountResetBody = z.object({
  hub_id: z.string().uuid(),
  nav_item: navItemSchema,
  conversation_id: z.string().uuid().optional(),
  hub_user_id: z.string().optional(),
  current_team_user_id: z.string().optional(),
});

export const navItemCountResetBody = z.object({
  nav_item: navItemSchema,
});

// ---------------------------------------------------------------------------
// Typing Indicator
// ---------------------------------------------------------------------------

export const typingBody = z.object({
  conversationId: z.string().uuid(),
  hubId: z.string().uuid(),
  action: z.enum(['start', 'stop']),
});

// ---------------------------------------------------------------------------
// WebSocket liveness frames (edge auto-response contract)
// ---------------------------------------------------------------------------

/**
 * Canonical client→server liveness ping frame, pinned as an EXACT byte string.
 *
 * The live-update client self-pings this frame every 25s. The LiveUpdateSession
 * DO registers it as a WebSocket auto-response request
 * (`ctx.setWebSocketAutoResponse`) so the Cloudflare edge answers with
 * {@link WS_SERVER_PONG_FRAME} WITHOUT waking the DO — the mechanism that makes an
 * idle-but-connected session cost ~$0.
 *
 * Auto-response matches the request frame BYTE-FOR-BYTE, so this string is a hard
 * contract shared by the producer (client self-ping / focus probe) and the
 * registration (DO). Both are covered by a byte-equality test against
 * `JSON.stringify({ type: 'ping' })` — do NOT hand-edit its spacing or key order.
 */
export const WS_CLIENT_PING_FRAME = '{"type":"ping"}';

/** Canonical server→client pong frame — the edge auto-response reply to {@link WS_CLIENT_PING_FRAME}. */
export const WS_SERVER_PONG_FRAME = '{"type":"pong"}';

// ---------------------------------------------------------------------------
// WebSocket close-code registry (idle-park protocol, gh #3280)
//
// The idle-park codes below are named here because BOTH sides reason about them
// (the DO emits or the client interprets them, and the tests on each side assert
// the same number) — a drifted literal would silently break the park protocol.
// So are the client's own closes of a socket that stopped answering, which the
// client's notifications read back (a gap the server still counted).
//
// The remaining codes are emitted and consumed on one side only and stay as
// literals at their call site, but are RESERVED here so nothing reuses them:
//   1000 cleanup (client)          4009 host_away (desktop client: the OS
//   1011 handshake/send failure         reports the person away)
//   4002 auth_ok_timeout (client)  4010 session revoked (server)
// ---------------------------------------------------------------------------

/** Server liveness sweep culled a socket with no ping in CLIENT_TIMEOUT_MS (dead, not idle). */
export const WS_CLOSE_SWEEP_TIMEOUT = 4000;

/** Client closed a socket that received no frame for its liveness timeout: half-open. */
export const WS_CLOSE_RX_TIMEOUT = 4003;

/** Client's on-focus liveness probe got no answer: the socket is half-open. */
export const WS_CLOSE_PROBE_TIMEOUT = 4004;

/** The web port closed the socket when the browser reported the network offline. */
export const WS_CLOSE_NETWORK_OFFLINE = 4005;

/**
 * Client deliberately closed a hidden/backgrounded tab's socket after
 * HIDDEN_DISCONNECT_DELAY_MS. Quiet close — no reconnect until the tab is
 * visible/foregrounded again.
 */
export const WS_CLOSE_HIDDEN_IDLE = 4006;

/**
 * Client deliberately closed a VISIBLE tab's socket after
 * VISIBLE_IDLE_DISCONNECT_MS with no user input (mouse/keys/scroll/focus).
 * Quiet close — any input reconnects instantly. Exists because an open socket
 * keeps its DO's liveness sweep re-arming (Cost Invariants): an idle tab must not
 * hold a socket open.
 */
export const WS_CLOSE_VISIBLE_IDLE = 4007;

/**
 * Server-side idle park: the liveness sweep closed every socket on a DO that
 * saw neither a broadcast nor a fresh connect for SERVER_IDLE_PARK_MS. A browser
 * client that is locally idle parks quietly; a locally-active one reconnects
 * immediately (its fresh connect resets the server's window). The desktop app
 * (the 'host' presence source) always parks, until its person shows, focuses or
 * uses the window or comes back from away.
 */
export const WS_CLOSE_SERVER_IDLE_PARK = 4008;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface NavigationCounts {
  chat_count: number;
  support_count: number;
  task_count: number;
  /**
   * People threads with unread messages (gh #4661): a count of threads, not of messages, and
   * not reset by viewing the tab. Present only while the threads surface is on, and only for
   * an interactive sign-in session; absent too while the switch cannot be read (fail-closed).
   * Null when the people read failed: the count is unknown, so a client keeps the badge it
   * shows.
   */
  threads_count?: number | null;
}

/** GET /counts */
export interface GetNavigationCountsResponse {
  success: true;
  data: NavigationCounts;
}

/** POST /reset-hub */
export interface ResetHubCountResponse {
  success: true;
  message: string;
  updated: boolean;
}

/** POST /reset-nav */
export interface ResetNavItemCountResponse {
  success: true;
  message: string;
}

/** POST /typing */
export interface TypingResponse {
  success: true;
}
